import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { useDispatch, useSelector } from "react-redux";
import { RecipeScalingApiEndpoints } from "api/api-endpoints";
import EntityDialog from "rs-components/EntityDialog";
import TableElement from "../Elements/components/TableElement";
import { RsWorkspaceEntityType } from "shared/types";
import SpaceViewerElement from "../Elements/components/SpaceViewerElement";
import ProcessDataViewerElement from "../Elements/components/ProcessDataViewerElement";
import LineGraphElement from "../Elements/components/LineGraphElement";
import Loader from "components/Loader";
import { RequestTemplate, GetRestrictionsMethod } from "store/knowledge-base-item/knowledge-base-item.types";
import { restrictionsSelector, serverErrorsSelector, } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { getRestrictions } from "store/knowledge-base-item/knowledge-base-item.actions";
import { getFieldRestrictions, getRequestName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { incompleteSimulationsIdsSelector, workspaceCellModelsSelector, workspaceInsightsSelector, workspaceOptimizationsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { getActiveResultSetIdSelector, expandedActiveResultElementSelector, isResultElementFailedInCalculationSelector, duplicateTargetResultSetIdSelector, } from "store/rs-workspace/selectors/results.selectors";
import { duplicateResultSetElement, setActiveResultElement, setDuplicateTargetResultSetId, upsertResultElement, } from "store/rs-workspace/actions/results.actions";
import { ResultSetElementType, } from "store/rs-workspace/rs-workspace.types";
import { transformLineGraphElementValues, transformProcessDataViewerElementValues, transformSpaceviewerElementValues, } from "store/rs-workspace/utils/results.utils";
import { isNotEmpty } from "utils/validate";
import { processVariablesSelector } from "store/global/global.selectors";
import { isResultElementCompleted } from "store/rs-workspace/utils/completeness.utils";
import * as styles from "./styles.module.scss";
export var ElementFormField;
(function (ElementFormField) {
    ElementFormField["name"] = "name";
})(ElementFormField || (ElementFormField = {}));
export var TableElementFormField;
(function (TableElementFormField) {
    TableElementFormField["rows"] = "rows";
    TableElementFormField["columns"] = "columns";
})(TableElementFormField || (TableElementFormField = {}));
export var SpaceviewerElementFormField;
(function (SpaceviewerElementFormField) {
    SpaceviewerElementFormField["parameters"] = "parameters";
    SpaceviewerElementFormField["sources"] = "sources";
})(SpaceviewerElementFormField || (SpaceviewerElementFormField = {}));
export var LineGraphElementFormField;
(function (LineGraphElementFormField) {
    LineGraphElementFormField["parameter"] = "parameter";
    LineGraphElementFormField["sources"] = "sources";
})(LineGraphElementFormField || (LineGraphElementFormField = {}));
const getRestrictionsPayload = (type) => {
    const payload = { rsEndpoint: RecipeScalingApiEndpoints.workspaces, method: GetRestrictionsMethod.PUT };
    switch (type) {
        case ResultSetElementType.Table:
            return { ...payload, template: RequestTemplate.TABLE_ELEMENT_PUT };
        case ResultSetElementType.SpaceViewer:
            return { ...payload, template: RequestTemplate.SPACEVIEWER_ELEMENT_PUT };
        case ResultSetElementType.ProcessDataViewer:
            return { ...payload, template: RequestTemplate.PROCESS_DATA_VIEWER_ELEMENT_PUT };
        case ResultSetElementType.LineGraph:
            return { ...payload, template: RequestTemplate.LINE_GRAPH_ELEMENT_PUT };
        default:
            return {};
    }
};
const ElementDialog = () => {
    const dispatch = useDispatch();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { element, index, initialValues } = useSelector(expandedActiveResultElementSelector);
    const resultSetId = useSelector(getActiveResultSetIdSelector);
    const duplicateTargetResultSetId = useSelector(duplicateTargetResultSetIdSelector);
    const restrictions = useSelector(restrictionsSelector(getRestrictionsPayload(element?.type)));
    const simulations = useSelector(workspaceSimulationsSelector);
    const insights = useSelector(workspaceInsightsSelector);
    const optimizations = useSelector(workspaceOptimizationsSelector);
    const processVariables = useSelector(processVariablesSelector);
    const cellModels = useSelector(workspaceCellModelsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const incompleteSimulationsIds = useSelector(incompleteSimulationsIdsSelector);
    const serverErrors = useSelector(serverErrorsSelector(getRestrictionsPayload(element?.type).template));
    const isErrored = useSelector(isResultElementFailedInCalculationSelector(resultSetId, element?.id));
    const isCompleted = useCallback((values) => isResultElementCompleted({ ...values, type: element?.type }, cellModels, recipes, simulations, insights, processVariables, incompleteSimulationsIds, optimizations), [
        cellModels,
        element?.type,
        incompleteSimulationsIds,
        insights,
        optimizations,
        processVariables,
        recipes,
        simulations,
    ]);
    const renderElement = useCallback((form, values) => {
        if (resultSetId === null) {
            return null;
        }
        switch (element?.type) {
            case ResultSetElementType.Table:
                return (React.createElement(TableElement, { resultSetId: resultSetId, elementId: element?.id, form: form, elementData: values }));
            case ResultSetElementType.SpaceViewer:
                return (React.createElement(SpaceViewerElement, { resultSetId: resultSetId, elementId: element.id, form: form, elementData: { ...element, ...values }, restrictions: restrictions, isCompleted: isCompleted(values) }));
            case ResultSetElementType.ProcessDataViewer:
                return (React.createElement(ProcessDataViewerElement, { resultSetId: resultSetId, form: form, elementData: { ...element, ...values }, restrictions: restrictions }));
            case ResultSetElementType.LineGraph:
                return (React.createElement(LineGraphElement, { resultSetId: resultSetId, form: form, elementData: { ...element, ...values }, restrictions: restrictions }));
        }
    }, [element, isCompleted, restrictions, resultSetId]);
    const onDialogClose = useCallback((form) => {
        dispatch(setActiveResultElement(null));
        dispatch(setDuplicateTargetResultSetId(null));
        form.change(ElementFormField.name, "");
    }, [dispatch]);
    const onSubmit = useCallback((values) => {
        setIsSubmitting(true);
        const { type, id: elementId } = element || {};
        if (resultSetId === null || type === undefined || elementId === undefined) {
            return;
        }
        let data;
        switch (element?.type) {
            case ResultSetElementType.Table:
                data = values;
                break;
            case ResultSetElementType.SpaceViewer:
                data = transformSpaceviewerElementValues(values);
                break;
            case ResultSetElementType.ProcessDataViewer:
                data = transformProcessDataViewerElementValues(values);
                break;
            case ResultSetElementType.LineGraph:
                data = transformLineGraphElementValues(values);
                break;
        }
        if (isNotEmpty(duplicateTargetResultSetId)) {
            dispatch(duplicateResultSetElement({ ...element, ...data }));
        }
        else {
            dispatch(upsertResultElement({ resultSetId, elementId, data, type }));
        }
        setIsSubmitting(false);
    }, [dispatch, element, resultSetId, duplicateTargetResultSetId]);
    const getResultElementType = useMemo(() => {
        switch (element?.type) {
            case ResultSetElementType.Table:
                return RsWorkspaceEntityType.TabularView;
            case ResultSetElementType.SpaceViewer:
                return RsWorkspaceEntityType.SpaceViewer;
            case ResultSetElementType.ProcessDataViewer:
                return RsWorkspaceEntityType.ProcessDataViewer;
            case ResultSetElementType.LineGraph:
                return RsWorkspaceEntityType.LineGraph;
            default:
                return RsWorkspaceEntityType.ResultSetElement;
        }
    }, [element?.type]);
    useEffect(() => {
        serverErrors && setIsSubmitting(false);
        !restrictions && dispatch(getRestrictions(getRestrictionsPayload(element?.type)));
    }, [dispatch, element?.type, restrictions, serverErrors]);
    return (React.createElement(Form, { onSubmit: onSubmit, initialValues: initialValues, mutators: { ...arrayMutators }, validateOnBlur: true, keepDirtyOnReinitialize: true, render: ({ handleSubmit, form, values, ...renderProps }) => (React.createElement(EntityDialog, { nameFieldName: ElementFormField.name, nameFieldRestrictions: getFieldRestrictions(restrictions, [getRequestName(ElementFormField.name)]), entityType: getResultElementType, isOpen: true, onDialogClose: () => onDialogClose(form), onClosing: async () => {
                await form.reset();
                setIsSubmitting(false);
            }, handleSubmit: handleSubmit, formRenderProps: renderProps, entityCompleted: isCompleted(values), entityErrored: isErrored, index: index, selectedId: element?.id ?? -1, enableSubmitIfPristine: isNotEmpty(duplicateTargetResultSetId) },
            React.createElement("div", { className: styles.body }, isSubmitting ? (React.createElement("div", { className: styles["loader-container"] },
                React.createElement(Loader, { size: 40 }))) : (renderElement(form, values))))) }));
};
export default ElementDialog;
